import React, { useEffect, useState } from 'react';
import { Check, Lock } from 'lucide-react';
import type { OnboardingStepProps } from '../onboardingSteps';
import { fetchUserThemes, activateTheme } from '../onboardingApi';
import { usePlan } from '@/context/PlanContext';
import axios from '@/utils/axiosInstance';
import { Button } from '@/components/ui/button';

interface ThemeItem {
  _id: string;
  themeName: string;
  isActive: boolean;
  masterTheme?: {
    _id: string;
    title: string;
    image: string;
    isPaid: boolean;
    price: number;
  };
}

const ThemeOnboarding: React.FC<OnboardingStepProps> = ({ saveRef, onValidationChange, onPreviewChange }) => {
  const [themes, setThemes] = useState<ThemeItem[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [defaultFreeThemeIds, setDefaultFreeThemeIds] = useState<string[]>([]);
  const { isUnpaid, lifecycle, openUpgradeModal, loading: planLoading } = usePlan();
  const hasThemeAccess = !isUnpaid || lifecycle === 'onboarding';

  useEffect(() => {
    const load = async () => {
      try {
        // Fetch global settings and themes in parallel
        const [data, settingsRes] = await Promise.all([
          fetchUserThemes(),
          axios.get('/api/domain/settings/global').catch(() => null),
        ]);
        const freeIds = [
          ...(settingsRes?.data?.defaultFreeThemes || []).map((id: any) => id.toString()),
          ...(settingsRes?.data?.defaultFreeTheme ? [settingsRes.data.defaultFreeTheme.toString()] : []),
        ].filter((id, i, arr) => arr.indexOf(id) === i);
        setDefaultFreeThemeIds(freeIds);
        const themeList = (Array.isArray(data) ? data : data.themes || []) as ThemeItem[];
        setThemes(themeList);

        // Pre-select: primary free theme (first in array) → active theme → first theme
        const defaultTheme = freeIds[0]
          ? themeList.find((t) => t.masterTheme?._id === freeIds[0])
          : null;
        const active = themeList.find((t) => t.isActive);
        setSelectedId(defaultTheme?._id || active?._id || themeList[0]?._id || null);
      } catch {
        // Failed to load themes
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // When plan info finishes loading, ensure the selected theme isn't locked
  useEffect(() => {
    if (planLoading || themes.length === 0 || !selectedId) return;
    const selected = themes.find((t) => t._id === selectedId);
    if (!selected) return;
    const isDefault = defaultFreeThemeIds.includes(selected.masterTheme?._id || '');
    const isLocked = !hasThemeAccess && !isDefault;
    if (isLocked) {
      // Reset to the primary free theme, or the first unlocked one
      const defaultTheme = defaultFreeThemeIds[0]
        ? themes.find((t) => t.masterTheme?._id === defaultFreeThemeIds[0])
        : null;
      const firstUnlocked = themes.find(
        (t) => hasThemeAccess || defaultFreeThemeIds.includes(t.masterTheme?._id || '')
      );
      setSelectedId(defaultTheme?._id || firstUnlocked?._id || null);
    }
  }, [planLoading, hasThemeAccess, themes, defaultFreeThemeIds, selectedId]);

  // Always valid — there's always a selection
  useEffect(() => {
    onValidationChange(selectedId !== null);
  }, [selectedId, onValidationChange]);

  useEffect(() => {
    if (loading || !selectedId) return;
    const selected = themes.find((theme) => theme._id === selectedId);
    const themeName = selected?.masterTheme?.title || selected?.themeName;
    if (themeName) onPreviewChange?.({ theme: { themeName } });
  }, [loading, selectedId, themes, onPreviewChange]);

  // Expose save function to parent via ref
  useEffect(() => {
    saveRef.current = async () => {
      if (selectedId) {
        await activateTheme(selectedId);
      }
    };
  }, [saveRef, selectedId]);

  if (loading || planLoading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-48 bg-gray-100 rounded-xl animate-pulse" />
        ))}
      </div>
    );
  }

  if (themes.length === 0) {
    return (
      <div className="text-center py-6 sm:py-12">
        <p className="text-gray-500">No themes are available yet. You can skip this step.</p>
      </div>
    );
  }

  // Mirror the ShowcaseOnboarding pattern: split into unlocked and locked,
  // render locked cards visually disabled with NO per-card upgrade button,
  // and surface a single floating sticky upgrade CTA between the two
  // sections so it stays visible while scrolling through locked themes.
  const themesWithMeta = themes.map((theme, index) => ({
    theme,
    isDefault: defaultFreeThemeIds.includes(theme.masterTheme?._id || ''),
    tierBadge: index < 10 ? 'Pro' : 'Pro & Pro Max',
  }));
  const unlockedThemes = themesWithMeta.filter((t) => hasThemeAccess || t.isDefault);
  const lockedThemes = themesWithMeta.filter((t) => !hasThemeAccess && !t.isDefault);
  const lockedCount = lockedThemes.length;

  // Inline render for one theme card — defined once so we can render it in
  // both the unlocked and locked grids without duplication.
  const renderThemeCard = ({
    theme,
    isDefault,
    tierBadge,
  }: {
    theme: ThemeItem;
    isDefault: boolean;
    tierBadge: string;
  }) => {
    const isSelected = selectedId === theme._id;
    const isLocked = !hasThemeAccess && !isDefault;
    const name = theme.masterTheme?.title || theme.themeName;
    const image = theme.masterTheme?.image;

    return (
      <div
        key={theme._id}
        className={`relative rounded-xl border-2 overflow-hidden transition-all duration-200 flex flex-col ${
          isLocked
            ? 'border-gray-200 opacity-80'
            : isSelected
              ? 'border-primary ring-2 ring-primary/20 shadow-md'
              : 'border-gray-200 hover:border-gray-300 hover:shadow-none'
        }`}
        style={{ contentVisibility: 'auto', containIntrinsicSize: '0 280px' }}
      >
        {/* Theme preview image — clickable to activate. Many users instinctively
            tap the thumbnail rather than the small button below, so we expose
            the whole image as a tap target. The Activate button is kept as
            a redundant explicit affordance. */}
        <div
          className={`aspect-video bg-muted relative overflow-hidden group/thumb ${
            isLocked ? 'cursor-pointer' : 'cursor-pointer'
          }`}
          role="button"
          tabIndex={0}
          aria-label={isLocked ? `Unlock ${name}` : `Activate ${name}`}
          onClick={() => {
            if (isLocked) {
              openUpgradeModal('themes');
            } else {
              setSelectedId(theme._id);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              if (isLocked) {
                openUpgradeModal('themes');
              } else {
                setSelectedId(theme._id);
              }
            }
          }}
        >
          {image ? (
            <img
              src={image}
              alt={name}
              className={`w-full h-full object-cover transition-transform duration-200 ${
                isLocked ? 'blur-[1px] brightness-75' : 'group-hover/thumb:scale-[1.03]'
              }`}
              loading="lazy"
              decoding="async"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
              <span className="text-gray-400 text-sm">{name}</span>
            </div>
          )}

          {/* Hover affordance — shows what tapping will do. The label fades
              in on hover/focus so the thumbnail is the primary tap target
              without cluttering the resting state. */}
          {!isLocked && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover/thumb:bg-black/30 transition-colors duration-200 pointer-events-none">
              <span className="opacity-0 group-hover/thumb:opacity-100 transition-opacity duration-200 text-white text-xs font-medium bg-black/60 px-2.5 py-1 rounded-md flex items-center gap-1">
                <Check className="w-3 h-3" />
                {isSelected ? 'Already active' : 'Tap to activate'}
              </span>
            </div>
          )}

          {/* Locked overlay — visual only, no per-card upgrade button. The
              single sticky CTA between sections surfaces the upsell. */}
          {isLocked && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <Lock className="w-4 h-4 text-white/80" />
            </div>
          )}

          {/* Badges — top-right */}
          <div className="absolute top-2 right-2 flex items-center gap-1.5 pointer-events-none">
            <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold bg-black/60 text-white backdrop-blur-xs shadow-xs border border-white/10">
              {tierBadge}
            </span>
            {isSelected && !isLocked && (
              <span className="bg-primary text-primary-foreground px-2 py-0.5 rounded-md text-xs font-medium flex items-center gap-1 shadow-xs">
                <Check className="w-3 h-3" />
                Active
              </span>
            )}
          </div>
        </div>

        {/* Theme name + action */}
        <div className="p-3 sm:p-4 space-y-3 flex flex-col flex-1">
          <h3 className={`font-semibold text-sm sm:text-base truncate ${isLocked ? 'text-muted-foreground' : 'text-gray-700'}`}>{name}</h3>
          <div className="flex gap-2 mt-auto">
            {isLocked ? (
              // Single upgrade CTA sits between the two grids; locked cards
              // show no action button so the user isn't pummeled with repeats.
              <div className="flex-1 h-8" />
            ) : isSelected ? (
              <Button size="sm" variant="secondary" className="flex-1" disabled>
                <Check className="size-3.5 mr-1" />
                Active
              </Button>
            ) : (
              <Button size="sm" className="flex-1" onClick={() => setSelectedId(theme._id)}>
                <Check className="size-3.5 mr-1" />
                Activate
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4 onb-fade-in">
      {/* Header counter — mirrors ShowcaseOnboarding's "X active · Y locked" line */}
      {unlockedThemes.length > 0 && (
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <span>
            <span className="font-medium text-foreground">{unlockedThemes.length}</span> available
            {lockedCount > 0 && (
              <>
                {' · '}
                <span className="font-medium text-muted-foreground">{lockedCount} locked</span>
              </>
            )}
          </span>
        </div>
      )}

      {/* Unlocked (free) themes */}
      {unlockedThemes.length > 0 && (
        <div className="grid grid-cols-2 gap-3 sm:gap-4">
          {unlockedThemes.map(renderThemeCard)}
        </div>
      )}

      {/* Floating sticky upgrade CTA — sits below free themes and on top of
          locked themes, stays pinned while scrolling through locked content. */}
      {lockedCount > 0 && (
        <div className="sticky top-20 z-20 py-3">
          <div className="flex justify-center">
            <Button
              variant="default"
              size="lg"
              className="shadow-xl bg-primary/95 backdrop-blur-sm"
              onClick={() => openUpgradeModal('themes')}
            >
              <Lock className="w-4 h-4 mr-2" />
              Upgrade to unlock {lockedCount} theme{lockedCount > 1 ? 's' : ''}
            </Button>
          </div>
        </div>
      )}

      {/* Locked themes */}
      {lockedCount > 0 && (
        <div className="grid grid-cols-2 gap-3 sm:gap-4">
          {lockedThemes.map(renderThemeCard)}
        </div>
      )}
    </div>
  );
};

export default ThemeOnboarding;
